import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import InlineSVG from 'svg-inline-react';

import Button from 'src/app/components/Elements/Button';
import type { TransitionStateType } from 'src/app/types';

import LinkButton from '../Button/LinkButton';
import LinkWithQuery from '../LinkWithQuery';

export const Overlay = styled.div<{ isHiddenOnLargeScreens: boolean; isShown: boolean }>`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: ${props => (props.isShown ? props.theme.opacities.overlay : props.theme.opacities.none)};
  background-color: ${props => props.theme.colors.capsuleBlue50};
  z-index: ${props => props.theme.zIndices.overlay};
  position: ${props => (props.isShown ? 'fixed' : '')};

  ${props => props.theme.mediaQueries.medium} {
    display: ${props => (props.isHiddenOnLargeScreens ? 'none' : 'block')};
  }
`;

export const Menu = styled.div<{
  hasGreenBackground: boolean;
  isHiddenOnLargeScreens: boolean;
  transitionState: TransitionStateType;
}>`
  position: fixed;
  display: flex;
  top: 0;
  height: 100vh;
  left: 0;
  width: ${({ theme }) => theme.space.loggedInLayoutSideNavWidth};
  flex-direction: column;
  box-shadow: ${props => props.theme.shadows.dp2};
  overflow-y: auto;
  z-index: ${props => props.theme.zIndices.overlayMore};
  background-color: ${props =>
    props.hasGreenBackground ? props.theme.colors.capsuleGreen00 : props.theme.colors.white};
  transform: ${({ transitionState }) => (transitionState === 'entered' ? 'translateX(0%)' : ' translateX(-100%)')};
  transition: ${props => props.theme.animationSpeeds.fast} ease-in;

  & a {
    color: ${({ theme }) => theme.colors.capsuleBlue50};
    text-decoration: none;
  }

  ${props => props.theme.mediaQueries.medium} {
    display: ${props => (props.isHiddenOnLargeScreens ? 'none' : 'flex')};
  }
`;

export const CloseButtonContainer = styled.div`
  position: absolute;
  right: 0;

  button {
    background-color: transparent;
    border: none;
    padding: ${props => props.theme.space.s2};
  }
`;

export const CloseButton = styled(InlineSVG)`
  display: inline-block;
  width: 2rem;
  height: 2rem;
  svg {
    width: 1.2rem;
    height: 1.2rem;
    margin: 0.4rem;
    fill: ${props => props.theme.colors.capsuleGray50};
  }
`;

export const Logo = styled.div<{ isLogoPaddedOnLeftSide: boolean }>`
  height: 8rem;
  display: flex;
  align-items: center;
  padding-left: ${props => (props.isLogoPaddedOnLeftSide ? props.theme.space['6.6'] : '')};
  justify-content: ${props => (props.isLogoPaddedOnLeftSide ? '' : 'center')};
  width: ${props => (props.isLogoPaddedOnLeftSide ? '' : '80%')};
`;

export const SectionHeader = styled.h2`
  font-family: ${props => props.theme.fonts.circular};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  text-transform: uppercase;
  line-height: ${props => props.theme.lineHeights.small};
  letter-spacing: ${props => props.theme.letterSpacings.one} !important;
  color: ${props => props.theme.colors.capsuleGreen60} !important;
  font-size: ${props => props.theme.fontSizes.small} !important;
  margin: 0;
  padding-left: ${props => props.theme.space.s6} !important;
  padding-bottom: ${props => props.theme.space.s2} !important;
`;

export const MenuLink = styled(props => <LinkWithQuery {...props} />)`
  font-family: ${props => props.theme.fonts.circular};
  font-weight: ${props => props.theme.fontWeights.semiBold} !important;
  line-height: ${props => props.theme.lineHeights.body};
  letter-spacing: ${props => props.theme.letterSpacings.normal};
  font-size: ${props => props.theme.fontSizes.body} !important;
  padding-bottom: ${props => props.theme.space.s5};
  padding-left: ${props => props.theme.space.s6};
  color: ${props => props.theme.colors.capsuleBlue50} !important;
`;

export const MenuHyperLink = styled.a`
  font-family: ${props => props.theme.fonts.circular};
  font-weight: ${props => props.theme.fontWeights.semiBold} !important;
  line-height: ${props => props.theme.lineHeights.body};
  letter-spacing: ${props => props.theme.letterSpacings.normal};
  font-size: ${props => props.theme.fontSizes.body} !important;
  padding-bottom: ${props => props.theme.space.s1};
  padding-left: ${props => props.theme.space.s6};
  color: ${props => props.theme.colors.capsuleBlue50} !important;
`;

export const DecoratedLink = styled(NavLink)`
  font-family: ${props => props.theme.fonts.circular};
  font-weight: ${props => props.theme.fontWeights.semiBold} !important;
  line-height: ${props => props.theme.lineHeights.body};
  letter-spacing: ${props => props.theme.letterSpacings.normal};
  font-size: ${props => props.theme.fontSizes.body};
  padding-left: ${props => props.theme.space.s6};
  padding-top: ${props => props.theme.space['1.3']};
  padding-bottom: ${props => props.theme.space['1.3']};

  &.active {
    &:before {
      content: '';
      width: 0.5rem;
      height: 100%;
      background-color: ${props => props.theme.colors.capsuleGreen60};
      display: block;
      position: absolute;
      left: 0;
      top: 0;
    }

    position: relative;
    background-color: ${props => props.theme.colors.capsuleGreen10};
  }
`;

export const MenuButton = styled(Button)`
  margin-left: ${props => props.theme.space.s6};
  margin-right: ${props => props.theme.space.s6};
  margin-bottom: ${props => props.theme.space.s6};
`;

export const MenuButtonStyleLink = styled(LinkButton)`
  margin-left: ${props => props.theme.space.s6};
  margin-right: ${props => props.theme.space.s6};
  margin-bottom: ${props => props.theme.space['1.4']};
`;
